import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAdminAuth } from '../../store/slices/adminAuthSlice';
import subscriptionApi from '../../api/subscription';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Skeleton
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { fetchAdminPlans, selectAdminPlans, selectAdminPlansLoading, selectAdminPlansError, clearPlans } from '../../store/slices/adminPlansSlice';

function SkeletonTableRows({ rows = 5, columns = 6 }) {
  return Array.from({ length: rows }).map((_, i) => (
    <TableRow key={i}>
      {Array.from({ length: columns }).map((_, j) => (
        <TableCell key={j}><Skeleton variant="rectangular" height={28} /></TableCell>
      ))}
    </TableRow>
  ));
}

function SubscriptionPlans() {
  const dispatch = useDispatch();
  const { token: adminToken } = useSelector(selectAdminAuth);
  const plans = useSelector(selectAdminPlans);
  const loading = useSelector(selectAdminPlansLoading);
  const error = useSelector(selectAdminPlansError);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addType, setAddType] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  // Fetch plans on component mount
  useEffect(() => {
    if (adminToken && plans.length === 0 && !loading && !hasFetched) {
      dispatch(fetchAdminPlans(adminToken));
      setHasFetched(true);
    }
  }, [adminToken, plans.length, loading, dispatch, hasFetched]);

  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      const body = {
        name: formData.name,
        type: formData.type,
        unit_label: 'requests',
        description: formData.description,
        included_units: formData.is_custom ? undefined : Number(formData.units),
        base_price: formData.is_custom ? undefined : formData.price,
        currency: formData.currency || 'usd',
        interval: formData.interval || (formData.type === 'one_time' ? 'one_time' : 'month'),
        is_custom: !!formData.is_custom,
        overage_price_per_unit: formData.overage_price_per_unit,
        overage_ceiling: formData.overage_ceiling,
      };
      Object.keys(body).forEach(key => body[key] === undefined && delete body[key]);

      if (editingPlan && editingPlan.id) {
        await subscriptionApi.updatePlan(adminToken, editingPlan.id, body);
        toast.success('Plan updated successfully!');
      } else {
        await subscriptionApi.createPlan(adminToken, body);
        toast.success('Plan created successfully!');
      }
      dispatch(clearPlans());
      await dispatch(fetchAdminPlans(adminToken));
      setShowAddForm(false);
      setEditingPlan(null);
      setAddType(null);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to save plan');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setShowAddForm(false);
    setAddType(null);
    setTimeout(() => setShowAddForm(true), 0);
  };

  const handleCreate = (type) => {
    setEditingPlan(null);
    setAddType(type);
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingPlan(null);
    setAddType(null);
  };

  const sortPlans = (plansList) => {
    return plansList.sort((a, b) => {
      if (a.is_custom && !b.is_custom) return 1;
      if (!a.is_custom && b.is_custom) return -1;
      
      const aPrice = Number(a.base_price || a.price || 0);
      const bPrice = Number(b.base_price || b.price || 0);
      return aPrice - bPrice;
    });
  };

  const subscriptionPlans = sortPlans(
    plans.filter(plan => plan.type === 'subscription' || plan.type === 'recurring')
  );
  const oneTimePlans = sortPlans(
    plans.filter(plan => plan.type === 'one_time' || plan.type === 'one-time')
  );

  const hasCustomSubscription = subscriptionPlans.some(plan => plan.is_custom);
  const hasCustomOneTime = oneTimePlans.some(plan => plan.is_custom);

  // Updated delete logic to use dialog
  const handleDeleteClick = (plan) => {
    setPlanToDelete(plan);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    if (planToDelete && planToDelete.id) {
      try {
        await subscriptionApi.deletePlan(adminToken, planToDelete.id);
        dispatch(clearPlans());
        await dispatch(fetchAdminPlans(adminToken));
        toast.success('Plan deleted successfully!');
      } catch (err) {
        toast.error(err.response?.data?.error || err.message || 'Failed to delete plan');
      }
    }
    setIsDeleting(false);
    setDeleteDialogOpen(false);
    setPlanToDelete(null);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold" mb={3}>
          Plans Management
        </Typography>
        <Paper sx={{ mb: 4, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">
              Subscription Plans
            </Typography>
            <Skeleton variant="rectangular" width={220} height={36} />
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Plan Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Base Price</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Included Units</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Overage Price/Unit</TableCell>
                  <TableCell sx={{ fontWeight: 'bold'}}> Overage Ceiling </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <SkeletonTableRows rows={5} columns={6} />
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <Paper sx={{ overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: 'secondary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">
              One-Time Plans
            </Typography>
            <Skeleton variant="rectangular" width={220} height={36} />
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Plan Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Base Price</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Included Units</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Overage Price/Unit</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <SkeletonTableRows rows={5} columns={6} />
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" fontWeight="bold" mb={3}>
        Plans Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Subscription Plans Section */}
      <Paper sx={{ mb: 4, overflow: 'hidden' }}>
        <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            Subscription Plans
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleCreate('subscription')}
            sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' }, borderRadius: 1, fontSize: 14}}
          >
            Add New Subscription Plan
          </Button>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Plan Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Base Price</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Included Units</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Overage Price/Unit</TableCell>
                <TableCell sx={{ fontWeight: 'bold'}}> Overage Ceiling </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                {/* <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell> */}
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptionPlans.length > 0 ? (
                subscriptionPlans.map((plan) => (
                  <TableRow key={plan.id || plan._id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {plan.name || plan.plan_name}
                        {plan.is_custom && (
                          <Chip 
                            label="Custom" 
                            size="small" 
                            color="secondary" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{plan.type}</TableCell>
                    <TableCell>
                      {plan.is_custom ? 'Custom' : `$${plan.base_price || plan.price || 0}`}
                    </TableCell>
                    <TableCell>
                      {plan.is_custom ? 'Custom' : (plan.included_units || plan.units || 0)}
                    </TableCell>
                    <TableCell>
                      {plan.is_custom ? 'Custom' : (plan.overage_price_per_unit || 0)}
                    </TableCell>
                    <TableCell>
                      {plan.is_custom ? 'Custom' : (plan.overage_ceiling || 0)}
                    </TableCell>
                    <TableCell>
                      {plan.description || 'No description'}
                    </TableCell>
                    {/* <TableCell>
                      <Chip
                        label={plan.is_active ? 'Active' : 'Inactive'}
                        color={plan.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell> */}
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEdit(plan)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(plan)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No subscription plans found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* One-Time Plans Section */}
      <Paper sx={{ overflow: 'hidden' }}>
        <Box sx={{ p: 2, bgcolor: 'secondary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            One-Time Plans
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleCreate('one_time')}
            sx={{ bgcolor: 'white', color: 'secondary.main', '&:hover': { bgcolor: 'grey.100' }, borderRadius: 1, fontSize: 14 }}
          >
            Add New One-Time Plan
          </Button>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Plan Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Base Price</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Included Units</TableCell>
                {/* <TableCell sx={{ fontWeight: 'bold' }}>Overage Price/Unit</TableCell> */}
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                {/* <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell> */}
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {oneTimePlans.length > 0 ? (
                oneTimePlans.map((plan) => (
                  <TableRow key={plan.id || plan._id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {plan.name || plan.plan_name}
                        {plan.is_custom && (
                          <Chip 
                            label="Custom" 
                            size="small" 
                            color="secondary" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{plan.type}</TableCell>
                    <TableCell>
                      {plan.is_custom ? 'Custom' : `$${plan.base_price || plan.base_price || 0}`}
                    </TableCell>
                    <TableCell>
                      {plan.is_custom ? 'Custom' : (plan.units || plan.included_units || 0)}
                    </TableCell>
                    <TableCell>
                      {plan.description || 'No description'}
                    </TableCell>
                    {/* <TableCell>
                      <Chip
                        label={plan.is_active ? 'Active' : 'Inactive'}
                        color={plan.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell> */}
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEdit(plan)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(plan)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No one-time plans found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Plan Dialog */}
      <Dialog 
        open={showAddForm || !!editingPlan} 
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingPlan ? 'Edit Plan' : addType === 'subscription' ? 'Add New Subscription Plan' : 'Add New One-Time Plan'}
        </DialogTitle>
        <DialogContent>
          <PlanForm 
            plan={editingPlan}
            onSave={handleSave}
            onCancel={handleCancel}
            addType={addType}
            hasCustomForType={
              (addType === 'subscription' || editingPlan?.type === 'subscription')
                ? hasCustomSubscription
                : hasCustomOneTime
            }
            isSaving={isSaving}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Plan</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this plan?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={18} /> : null}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const INTERVAL_OPTIONS = ['day', 'week', 'month', 'year', 'one_time'];

function getDefaultInterval(type) {
  if (type === 'one_time') return 'one_time';
  return 'month'; // or 'day' if you prefer
}

// Plan Form Component
function PlanForm({ plan, onSave, onCancel, addType, hasCustomForType, isSaving }) {
  const [formData, setFormData] = useState({
    name: plan?.name || plan?.plan_name || '',
    type: plan?.type || addType || 'subscription',
    price: plan?.base_price || plan?.price || '',
    units: plan?.included_units || plan?.units || '',
    description: plan?.description || '',
    is_custom: plan?.is_custom || false,
    interval: plan?.interval || getDefaultInterval(plan?.type || addType),
    currency: plan?.currency || 'usd',
    overage_price_per_unit: plan?.overage_price_per_unit || '',
    overage_ceiling: plan?.overage_ceiling || ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (addType) {
      setFormData((prev) => ({ ...prev, type: addType }));
    }
  }, [addType]);

  useEffect(() => {
    if (hasCustomForType && !formData.is_custom) {
      setFormData((prev) => ({ ...prev, is_custom: false }));
    }
  }, [hasCustomForType]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Plan Name is required';
    if (!formData.type) newErrors.type = 'Type is required';
    if (!formData.interval) newErrors.interval = 'Interval is required';
    if (!formData.currency) newErrors.currency = 'Currency is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.is_custom) {
      if (!formData.price || isNaN(formData.price) || Number(formData.price) <= 0) {
        newErrors.price = "Price must be a positive number";
      }
      if (!formData.units || isNaN(formData.units) || !Number.isInteger(Number(formData.units)) || Number(formData.units) <= 0) {
        newErrors.units = "Units must be a positive integer";
      }
    }
    if (!formData.overage_price_per_unit || isNaN(formData.overage_price_per_unit) || Number(formData.overage_price_per_unit) < 0) {
      newErrors.overage_price_per_unit = "Overage per unit must be 0 or a positive number";
    }
    if (!formData.overage_ceiling || isNaN(formData.overage_ceiling) || Number(formData.overage_ceiling) < 0) {
      newErrors.overage_ceiling = "Overage Ceiling must be 0 or a positive number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSave(formData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'is_custom') {
      setFormData(prev => ({
        ...prev,
        is_custom: checked,
        price: checked ? '' : prev.price,
        units: checked ? '' : prev.units
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const isEdit = !!plan;

  // Only allow the interval from API or default for creation
  const intervalOptions = INTERVAL_OPTIONS.map(opt => ({
    value: opt,
    label: opt === 'one_time' ? 'one_time' : opt.charAt(0).toUpperCase() + opt.slice(1)
  }));

  return (
    <Box component="form" onSubmit={handleSave} sx={{ mt: 2 }}>
      <Stack spacing={3}>
        <TextField
          fullWidth
          label="Plan Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          error={!!errors.name}
          helperText={errors.name}
        />

        <FormControl fullWidth required error={!!errors.type}>
          <InputLabel>Type</InputLabel>
          <Select
            name="type"
            value={formData.type}
            onChange={handleChange}
            label="Type"
            disabled={!!addType || isEdit}
          >
            <MenuItem value="subscription">Subscription</MenuItem>
            <MenuItem value="one_time">One-Time</MenuItem>
          </Select>
          {errors.type && <Typography color="error" variant="caption">{errors.type}</Typography>}
        </FormControl>

        {/* Interval Dropdown */}
        <FormControl fullWidth required>
          <InputLabel>Interval</InputLabel>
          <Select
            name="interval"
            value={formData.interval}
            onChange={handleChange}
            label="Interval"
            disabled // always disabled, only show current value
          >
            {intervalOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Currency Dropdown */}
        <FormControl fullWidth required error={!!errors.currency}>
          <InputLabel>Currency</InputLabel>
          <Select
            name="currency"
            value={formData.currency}
            onChange={handleChange}
            label="Currency"
          >
            <MenuItem value="usd">USD</MenuItem>
          </Select>
          {errors.currency && <Typography color="error" variant="caption">{errors.currency}</Typography>}
        </FormControl>

        <TextField
          fullWidth
          label="Price"
          name="price"
          type="number"
          value={formData.is_custom ? '' : formData.price}
          onChange={handleChange}
          // inputProps={{ step: "0.01" }}
          required={!formData.is_custom}
          disabled={formData.is_custom || isEdit}
          placeholder={formData.is_custom ? 'Custom' : ''}
          error={!!errors.price}
          helperText={errors.price}
        />

        <TextField
          fullWidth
          label="Units"
          name="units"
          type="number"
          value={formData.is_custom ? '' : formData.units}
          onChange={handleChange}
          required={!formData.is_custom}
          disabled={formData.is_custom || isEdit}
          placeholder={formData.is_custom ? 'Custom' : ''}
          error={!!errors.units}
          helperText={errors.units}
        />
        {formData.type === 'subscription' && (
          <TextField
            fullWidth
            label="Overage Price per Unit (In Dollars $)"
            name="overage_price_per_unit"
            type="number"
            value={formData.overage_price_per_unit}
            onChange={handleChange}
            inputProps={{ step: "0.01", min: "0" }}
            placeholder="e.g., 0.05"
            helperText="Price charged per extra request above included units In Cents"
            required
          />
        )}
        {formData.type === 'subscription' && (
          <TextField 
            fullWidth
            label="Overage API Calls Threshold"
            name="overage_ceiling"
            type="number"
            value={formData.overage_ceiling}
            onChange={handleChange}
            placeholder="e.g., 1000"
            helperText="Number of API calls consumed in overgae for this plan/"
            required
          />
        )}
        <TextField
          fullWidth
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          multiline
          rows={3}
          required
          error={!!errors.description}
          helperText={errors.description}
        />

        <Tooltip
          title={hasCustomForType ? "A custom plan already exists for this type. Only one custom plan is allowed per type." : ""}
          disableHoverListener={!hasCustomForType}
        >
          <span>
            <FormControlLabel
              control={
                <Checkbox
                  name="is_custom"
                  checked={formData.is_custom}
                  onChange={handleChange}
                  disabled={hasCustomForType || isEdit}
                />
              }
              label="Custom Plan"
            />
          </span>
        </Tooltip>
      </Stack>

      <DialogActions sx={{ mt: 3 }}>
        <Button onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={18} /> : null}
        >
          {plan
            ? isSaving
              ? 'Updating...'
              : 'Update'
            : isSaving
              ? 'Creating...'
              : 'Create'}
        </Button>
      </DialogActions>
    </Box>
  );
}

export default SubscriptionPlans; 